/**
 * Pure auth helpers: payloads, validation, redirects (plan 4 R2).
 * No DOM. Covered by test/v2-auth.test.js.
 */

const AUTH_QUERY_KEYS = ['oauth', 'token', 'email'];

/** @param {string} pathname */
export function isAuthPath(pathname) {
  const path = String(pathname || '').split('?')[0].replace(/\/+$/, '') || '/';
  return path === '/app/login' || path === '/app/signup' || path === '/app/accept-invite';
}

/**
 * Keep OAuth / invite query keys; drop product_id and anything else.
 * @param {string} [search]
 */
export function authSearch(search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const next = new URLSearchParams();
  for (const key of AUTH_QUERY_KEYS) {
    const value = params.get(key);
    if (value != null && value !== '') next.set(key, value);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : '';
}

/** @param {string} [search] */
export function parseAuthSearch(search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  return {
    oauth: params.get('oauth') || '',
    token: params.get('token') || '',
    email: params.get('email') || '',
  };
}

/** @param {string} provider @param {string} [intent] */
export function oauthStartUrl(provider, intent) {
  const id = String(provider || '').toLowerCase();
  const why = intent === 'signup' ? 'signup' : 'login';
  return `/api/v1/auth/oauth/${encodeURIComponent(id)}?intent=${encodeURIComponent(why)}`;
}

/** @param {{ email?: string, password?: string }} form */
export function loginPayload(form) {
  return {
    email: String(form && form.email != null ? form.email : '').trim(),
    password: String(form && form.password != null ? form.password : ''),
  };
}

/** @param {{ orgName?: string, name?: string, email?: string, password?: string, chatLanguage?: string }} form */
export function signupPayload(form) {
  const chatLanguage = String(form && form.chatLanguage != null ? form.chatLanguage : 'en').trim() || 'en';
  return {
    orgName: String(form && form.orgName != null ? form.orgName : '').trim(),
    name: String(form && form.name != null ? form.name : '').trim(),
    email: String(form && form.email != null ? form.email : '').trim(),
    password: String(form && form.password != null ? form.password : ''),
    chatLanguage,
  };
}

/** @param {{ orgName?: string, name?: string, chatLanguage?: string }} form */
export function oauthCompletePayload(form) {
  const orgName = String(form && form.orgName != null ? form.orgName : '').trim();
  const name = String(form && form.name != null ? form.name : '').trim();
  const chatLanguage = String(form && form.chatLanguage != null ? form.chatLanguage : 'en').trim() || 'en';
  const body = { orgName, chatLanguage };
  if (name) body.name = name;
  return body;
}

/** @param {{ token?: string, email?: string, password?: string }} form */
export function inviteAcceptPayload(form) {
  return {
    token: String(form && form.token != null ? form.token : ''),
    email: String(form && form.email != null ? form.email : '').trim(),
    password: String(form && form.password != null ? form.password : ''),
  };
}

/**
 * @param {{ orgName?: string, name?: string, email?: string, password?: string }} form
 * @returns {string|null} Error message matching classic copy, or null when ok.
 */
export function validateSignup(form) {
  const body = signupPayload(form);
  if (!body.orgName || !body.name || !body.email || !body.password) {
    return 'All fields are required';
  }
  if (body.password.length < 6) return 'Password must be at least 6 characters';
  return null;
}

/**
 * @param {{ password?: string, confirm?: string }} form
 * @returns {string|null}
 */
export function validateInvitePasswords(form) {
  const password = String(form && form.password != null ? form.password : '');
  const confirm = String(form && form.confirm != null ? form.confirm : '');
  if (password !== confirm) return 'Passwords do not match';
  if (password.length < 6) return 'Password must be at least 6 characters';
  return null;
}

/** @param {string} name */
export function workspaceSlugPreview(name) {
  const slug = String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || 'your-workspace';
}

/**
 * Where to send the browser after a successful password/OAuth sign-in.
 * Classic used `#add-product` / `#dashboard`; v2 uses real paths.
 * @param {{ hasProducts?: boolean }} opts
 */
export function postLoginPath(opts) {
  return opts && opts.hasProducts ? '/app/dashboard' : '/app/products/new';
}

/**
 * Login OAuth error banner copy (same wording as classic).
 * @param {string} oauth
 * @returns {{ kind: string, htmlParts: { before: string, link?: string, after?: string } }|null}
 */
export function loginOauthBanner(oauth) {
  if (oauth === 'unknown') {
    return {
      kind: 'unknown',
      before: 'No BonnieOS account uses that email yet. ',
      link: 'Create a workspace',
      after: ' — continue with Google, Microsoft, or GitHub, then name your workspace.',
    };
  }
  if (oauth === 'deactivated') {
    return {
      kind: 'deactivated',
      before: 'That account is deactivated. Contact your admin.',
    };
  }
  return null;
}

export const NEXT_STEPS = [
  {
    n: '01',
    title: 'Add a product',
    copy: "A short intro, who it's for, and any docs you'd hand a new hire.",
    tag: 'Product context',
  },
  {
    n: '02',
    title: 'Connect one analytics source',
    copy: 'GA4, BigQuery or Mixpanel. Credentials are AES-256 encrypted and never sent to a model.',
    tag: 'Encrypted · never sent to a model',
  },
  {
    n: '03',
    title: "Read Bonnie's first take",
    copy: "She states what she understands, cites her sources, and lists what she still can't answer.",
    tag: 'First take',
  },
];

export const WORKSPACE_STEPS = [
  NEXT_STEPS[0],
  NEXT_STEPS[1],
  {
    n: '03',
    title: 'Invite your team',
    copy: 'Bring in teammates once the workspace is set up — from Settings → Team & access.',
    tag: 'Team',
  },
];
